import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { trackArtifactDownloadFailed, trackArtifactDownloadSuccess } from '@/analytics/utils/artifact-download-analytics'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { FileDownload, Folder, MatterFolder, XClose } from '@/assets/icons'
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'
import { Button } from '@/components/button/Button'
import { Header } from '@/components/header/Header'
import { IconButton } from '@/components/icon-button/IconButton'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { GlobalToastType, kToastDurationLongMs, kToastDurationMediumMs, kToastDurationShortMs, kTooltipHoverDelayMs } from '@/constants/constants-ui'
import { kFlushFailedMessage, kMatterArtifactNameFallback } from '@/matter-artifacts/constants'
import { downloadMatterArtifactAsDocx } from '@/matter-artifacts/fetch/download-matter-artifact-docx'
import { selectCurrentMatterArtifactVersionFromVersionId } from '@/matter-artifacts/store/selectors'
import { selectMatterById } from '@/matter/store/selectors'
import { matterPaths } from '@/routing/paths'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import type { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { useDraftExport } from '../context/draft-export-context'
import { useDraftWorkspace } from '../hooks/useDraftWorkspace'
import { useLeaveDraftOnSmallScreen } from '../hooks/useLeaveDraftOnSmallScreen'

type DraftHeaderProps = {
  /* 'standalone' is the full-width header above the content; 'card' sits inside the document card. */
  layout?: 'standalone' | 'card'
  /* Small screens use a compact icon-only close control instead of the labelled button. */
  isSmallScreen?: boolean
}

/**
 * The draft's breadcrumb header (matter › draft) with draft actions.
 */
export default function DraftHeader({ layout = 'standalone', isSmallScreen = false }: DraftHeaderProps) {
  const { matterId } = useParams<{ matterId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const { draftArtifactVersionId, closeDraft } = useDraftWorkspace()
  const leaveDraftOnSmallScreen = useLeaveDraftOnSmallScreen()
  const { onDownloadWithTemplate, onSave, onViewExports, isExporting, isLoadingExportActions = false, editorRef } = useDraftExport()
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false)
  const [viewExportsLoading, setViewExportsLoading] = useState(false)
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: matterId ?? null }))
  const currentMatterArtifactVersion = useAppSelector((state: RootState) => selectCurrentMatterArtifactVersionFromVersionId(state, draftArtifactVersionId))
  const isExportInFlight = downloadLoading || isExporting
  // Waiting for the actions holds the control too, but is not an export: its end moves no focus.
  const isExportControlBusy = isExportInFlight || isLoadingExportActions
  const exportButtonRef = useRef<HTMLButtonElement>(null)
  const draftTitle = currentMatterArtifactVersion?.name || kMatterArtifactNameFallback
  // Held until the version loads so a cold load does not flash the "Untitled" fallback.
  const draftTitleIfLoaded = currentMatterArtifactVersion ? draftTitle : ''

  // Loading disables the export control, which drops focus, so put it back once the export ends unless the
  // user has moved on themselves.
  const wasExportInFlightRef = useRef(isExportInFlight)
  useEffect(() => {
    const wasExportInFlight = wasExportInFlightRef.current
    wasExportInFlightRef.current = isExportInFlight
    if (!wasExportInFlight || isExportInFlight || document.activeElement !== document.body) return
    exportButtonRef.current?.focus()
  }, [isExportInFlight])

  const handleDownload = async () => {
    // The templated export handles the download when a template is selected or already applied; otherwise the draft downloads as-is.
    if (onDownloadWithTemplate) {
      onDownloadWithTemplate()
      return
    }
    if (!currentMatterArtifactVersion) return
    setDownloadLoading(true)
    let artifactVersionId: string | undefined
    try {
      // Clicking here blurs the editor, which starts a fire-and-forget autosave. Flushing it keeps the
      // download from delivering the pre-save version.
      const flushResult = await editorRef.current?.flushPendingSave()
      if (flushResult?.status === 'failed') {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: kFlushFailedMessage, durationMs: kToastDurationLongMs }))
        return
      }
      artifactVersionId = flushResult?.matterArtifactVersionId ?? currentMatterArtifactVersion.id
      const downloadedFileName = await downloadMatterArtifactAsDocx(artifactVersionId)
      dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Download complete: ${downloadedFileName}`, durationMs: kToastDurationShortMs }))
      trackArtifactDownloadSuccess(trackEvent, { artifactVersionId, artifactId: currentMatterArtifactVersion.matter_artifact_id })
    } catch {
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Download failed. Please try again.', durationMs: kToastDurationMediumMs }))
      if (artifactVersionId) {
        trackArtifactDownloadFailed(trackEvent, { artifactVersionId, artifactId: currentMatterArtifactVersion.matter_artifact_id })
      }
    } finally {
      setDownloadLoading(false)
    }
  }

  // The folder lookup can be slow, so the menu stays open with a spinner on the item until it
  // navigates or shows a toast.
  const handleViewExports = async (event: Event) => {
    if (!onViewExports) return
    event.preventDefault()
    setViewExportsLoading(true)
    try {
      await onViewExports()
    } finally {
      setViewExportsLoading(false)
      setActionsMenuOpen(false)
    }
  }

  if (!draftArtifactVersionId) return null

  const breadcrumbItems: BreadcrumbItemType[] = []
  if (matter) {
    breadcrumbItems.push({ id: `matter-${matter.id}`, label: matter.name, onClick: () => navigate(matterPaths.overview(matter.id)) })
  }
  breadcrumbItems.push({ id: `draft-${draftArtifactVersionId}`, label: draftTitle })

  // The card panel collapses the matter into the overflow menu (click to reveal).
  const cardBreadcrumbs = (
    <div className="flex-1 min-w-0 overflow-hidden">
      <Breadcrumbs items={breadcrumbItems} maxLabelLength={30} maxItems={2} tooltipPlacement="bottom-start" />
    </div>
  )

  const closeDraftControl = isSmallScreen ? (
    <div className="flex shrink-0 items-center pr-5">
      <IconButton variant="tonal" size="sm" icon={<XClose />} aria-label="Close draft" onClick={leaveDraftOnSmallScreen} />
    </div>
  ) : (
    <div className="flex shrink-0 items-center">
      <Button variant="tonal" color="neutral" size="xs" onClick={() => closeDraft()}>
        Close draft
      </Button>
      {/* Spacing sits on the divider. The card's right side is trimmed to offset the breadcrumb's own left padding; the
          Header cancels that padding itself on desktop, so the standalone side keeps its full width there. */}
      <div className={clsx('h-7 w-px bg-border-strong', layout === 'card' ? 'ml-5 mr-[14px]' : 'ml-6 mr-[18px] lg:mr-[26px]')} />
    </div>
  )

  const canSaveToMatterFiles = Boolean(onSave)
  const actionsIconSize = isSmallScreen || layout === 'card' ? 'md' : 'lg'

  const actions = canSaveToMatterFiles ? (
    <DropdownMenu.Root open={actionsMenuOpen} onOpenChange={setActionsMenuOpen}>
      <DropdownMenu.Trigger asChild disabled={isExportControlBusy}>
        <IconButton
          ref={exportButtonRef}
          variant="plain"
          color="neutral"
          size={actionsIconSize}
          icon={<FileDownload />}
          loading={isExportControlBusy}
          aria-label={isExportInFlight ? 'Exporting draft' : 'Download options'}
          tooltipText="Download options"
          tooltipPlacement="bottom"
          tooltipHoverDelayMs={kTooltipHoverDelayMs}
          disableTooltipInteractive
          disableTooltipFocusListener
        />
      </DropdownMenu.Trigger>
      <MenuContent className="min-w-[186px]" sideOffset={5} align="end">
        <MenuItemGroup>
          <MenuItem label="Download" icon={FileDownload} onSelect={handleDownload} variant="neutral" />
          <MenuItem label="Save copy to matter files" icon={Folder} onSelect={() => onSave?.()} variant="neutral" />
          {onViewExports && (
            <MenuItem
              label="View exports"
              icon={MatterFolder}
              loading={viewExportsLoading}
              onSelect={(event) => void handleViewExports(event)}
              variant="neutral"
            />
          )}
        </MenuItemGroup>
      </MenuContent>
    </DropdownMenu.Root>
  ) : (
    <IconButton
      ref={exportButtonRef}
      variant="plain"
      color="neutral"
      size={actionsIconSize}
      icon={<FileDownload />}
      loading={isExportControlBusy}
      aria-label={isExportInFlight ? 'Exporting draft' : 'Download draft'}
      tooltipText="Download draft"
      tooltipPlacement="bottom"
      tooltipHoverDelayMs={kTooltipHoverDelayMs}
      disableTooltipInteractive
      disableTooltipFocusListener
      onClick={handleDownload}
    />
  )

  if (layout === 'card') {
    return (
      <div className="flex h-12 shrink-0 items-center border-b bg-surface-content pl-3 pr-5">
        {closeDraftControl}
        {cardBreadcrumbs}
        <div className="shrink-0">{actions}</div>
      </div>
    )
  }

  return (
    <Header
      startContent={closeDraftControl}
      // Small screens show just the draft name (no matter crumb).
      {...(isSmallScreen ? { title: draftTitleIfLoaded, titleSize: 'sm' as const } : { breadcrumbs: breadcrumbItems })}
      endContent={actions}
    />
  )
}
